import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Bell, Check, Clock, X, Loader2, User, PawPrint } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { toast } from 'sonner';
import { format, parseISO, isPast } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SNOOZE_OPTS = [
  { label: '1 hour', minutes: 60 },
  { label: '3 hours', minutes: 180 },
  { label: 'Tomorrow', minutes: 1440 },
];

/**
 * RemindersList — compact widget showing due reminders with one-tap
 * Complete / Snooze / Dismiss. Reusable on Dashboard Home and Field View.
 */
export default function RemindersList({ scope = 'due', reloadSignal = 0, onChanged, hideWhenEmpty = false }) {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/dashboard/reminders`, {
        headers, params: { scope },
      });
      setReminders(data || []);
    } catch {
      setReminders([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, token]);

  useEffect(() => { load(); }, [load, reloadSignal]);

  const act = async (rid, action, body) => {
    setBusy((b) => ({ ...b, [rid]: action }));
    try {
      await axios.post(`${API_URL}/api/dashboard/reminders/${rid}/${action}`, body || {}, { headers });
      toast.success(action === 'complete' ? 'Reminder completed' : action === 'snooze' ? 'Snoozed' : 'Dismissed');
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update reminder');
    } finally {
      setBusy((b) => ({ ...b, [rid]: null }));
    }
  };

  if (loading) {
    return (
      <Card data-testid="reminders-widget">
        <CardContent className="py-6 flex justify-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  if (hideWhenEmpty && reminders.length === 0) return null;

  return (
    <Card data-testid="reminders-widget">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Bell className="h-5 w-5 text-primary" />
          Reminders
          {reminders.length > 0 && (
            <Badge className="bg-primary/10 text-primary border-primary/20 border" data-testid="reminders-count">
              {reminders.length}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {reminders.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2" data-testid="reminders-empty">
            You're all caught up — no reminders due.
          </p>
        ) : (
          reminders.map((r) => {
            const overdue = r.dueAt && isPast(parseISO(r.dueAt));
            return (
              <div
                key={r.id}
                className="flex items-start gap-2 rounded-md border p-2.5"
                data-testid={`reminder-${r.id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium leading-snug">{r.title}</p>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-muted-foreground">
                    {r.dueAt ? (
                      <span className={`flex items-center gap-1 ${overdue ? 'text-red-600 font-medium' : ''}`}>
                        <Clock className="h-3 w-3" />
                        {format(parseISO(r.dueAt), 'MMM d, h:mm a')}
                        {overdue ? ' · due' : ''}
                      </span>
                    ) : (
                      <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> No date</span>
                    )}
                    {r.linkedClientId && <span className="flex items-center gap-1"><User className="h-3 w-3" /> client</span>}
                    {r.linkedAnimalId && <span className="flex items-center gap-1"><PawPrint className="h-3 w-3" /> animal</span>}
                    {r.reminderType === 'RECURRING' && <Badge variant="outline" className="text-[10px]">recurring</Badge>}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-green-600"
                    onClick={() => act(r.id, 'complete')} disabled={!!busy[r.id]}
                    data-testid={`reminder-complete-${r.id}`} title="Complete">
                    {busy[r.id] === 'complete' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  </Button>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button size="icon" variant="ghost" className="h-8 w-8" disabled={!!busy[r.id]}
                        data-testid={`reminder-snooze-${r.id}`} title="Snooze">
                        <Clock className="h-4 w-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-36 p-1" align="end">
                      {SNOOZE_OPTS.map((o) => (
                        <button key={o.minutes} type="button" role="menuitem"
                          className="w-full text-left px-3 py-2 text-sm rounded hover:bg-muted"
                          onClick={() => act(r.id, 'snooze', { minutes: o.minutes })}
                          data-testid={`snooze-${o.minutes}-${r.id}`}>
                          {o.label}
                        </button>
                      ))}
                    </PopoverContent>
                  </Popover>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground"
                    onClick={() => act(r.id, 'dismiss')} disabled={!!busy[r.id]}
                    data-testid={`reminder-dismiss-${r.id}`} title="Dismiss">
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}
